import moment from "moment";
import environment from "flow/configuration/environment";

const standardInternalFormat = "YYYY-MM-DD";

const allowedCompletionFormats = [
    standardInternalFormat,
    "YYYY-MM",
    "YY-MM-DD",
    "YYYYMMDD",
    "YYMMDD",
    "YYYYMM",
    "YY-MM",
    "YYMM",
    "YYYY"
];
const completeDateFormats_SE = ["YYYY-MM-DD", "YY-MM-DD", "YYYYMMDD", "YYMMDD", "MM/DD/YYYY", "MM/DD/YY"];

const completeDateFormats_FI = ["D.M.YYYY", "DD.MM.YYYY", "DDMMYY"];

const completeDateFormats_NO = ["DD.MM.YYYY", "D.M.YYYY", "DDMMYY"];

const completeDateFormats_GB = ["DD/MM/YYYY"];

const supportedTimeFormats = ["HH", "HHmm", "HHmmss", "HH:mm:ss", "HH:mm", "HH,mm", "HH,mm,ss", "HH.mm", "HH.mm.ss"];

const serverSideTimeFormats = ["HH:mm:ss.SSS", "HH:mm:ss.SSSZ", "HH:mm:ss", "HH:mm"];

const serverSideDateFormat = "YYYY-MM-DD";

const serverSideDateTimeFormats = [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ssZ",
    "YYYY-MM-DDTHH:mm:ss"
];

const timeOutputFormat = "HH:mm";

function completeDate(dateString: string): Date {
    const date = moment(dateString, allowedCompletionFormats, true);

    if (date.isValid() === false) {
        throw new Error("Invalid date string");
    }

    return date.toDate();
}

function isCultureCountryFinland(): boolean {
    const culture = environment.getCulture();
    if (culture && culture.endsWith("-FI")) {
        return true;
    }
    return false;
}

function formatIfComplete(dateString: string): string {
    let date;
    let formattedDateString;
    const culture = environment.getCulture();

    if (isCultureCountryFinland()) {
        date = moment(dateString, completeDateFormats_FI, true);
        formattedDateString = date.format(getDateFormatForMoment());
    } else if (culture && culture.indexOf("nb-") !== -1) {
        date = moment(dateString, completeDateFormats_NO, true);
        formattedDateString = date.format("DD.MM.YYYY");
    } else if (culture && culture.indexOf("en-GB") !== -1) {
        date = moment(dateString, completeDateFormats_GB, true);
        formattedDateString = date.format("DD/MM/YYYY");
    } else {
        date = moment(dateString, completeDateFormats_SE, true);
        formattedDateString = getFormattedDateString(date);
    }

    if (date.isValid() === false) {
        return dateString;
    }

    return formattedDateString;
}

function calculateEndDate(startDate: moment.MomentInput): Date {
    return moment(startDate).endOf("month").toDate();
}

function getFormattedDateString(date?: moment.MomentInput): string {
    return moment(date, ["L", "L LTS", moment.ISO_8601]).format("L");
}

function getFormattedDateStringEmptyIfNonValidDate(date: string): string {
    if (isValidDateFormat(date)) {
        return moment(date).format("YYYY-MM-DD");
    }

    if (isValidDateAndTimeFormat(date)) {
        return moment(date).format("YYYY-MM-DD");
    }

    return "";
}

function getFormattedDateAndTimeString(date: moment.MomentInput): string {
    return moment(date, "L LTS").format("L LTS");
}

// Flows internal Person Id validator is relying on this validation
// Anything other format than ISO8601 returned here will cause issues
// If you want to do a culture sensitive date check use V2 instead
function isValidDateFormat(dateString: moment.MomentInput): boolean {
    if (isCultureCountryFinland()) {
        if (moment(dateString, getDateFormatForMoment(), true).isValid()) {
            return true;
        }
    }
    return moment(dateString, "YYYY-MM-DD", true).isValid();
}

// Culture sensitive format date check
function isValidDateFormatV2(dateString: moment.MomentInput): boolean {
    return moment(dateString, getDateFormatForMoment(), true).isValid();
}

function isValidShortDateFormat(dateString: moment.MomentInput): boolean {
    return moment(dateString, "YYYYMMDD", true).isValid();
}

function isValidYearFormat(dateString: moment.MomentInput): boolean {
    return moment(dateString, "YYYY", true).isValid();
}

function isValidDateAndTimeFormat(dateString: moment.MomentInput): boolean {
    return moment(dateString, "YYYY-MM-DD HH:mm:ss", true).isValid();
}

function isValidDateAndTimeFormatV2(dateString: moment.MomentInput): boolean {
    return moment(dateString, `${getDateFormatForMoment()} HH:mm:ss`, true).isValid();
}

function getValidTimeFormat(timeString: string): string {
    return supportedTimeFormats.find(function (format) {
        return moment(timeString, format, true).isValid();
    });
}

function isValidTimeFormat(timeString: string): boolean {
    return Boolean(getValidTimeFormat(timeString));
}

function toTime(timeString: string): string {
    const format = getValidTimeFormat(timeString);
    if (format !== undefined) {
        return moment(timeString, format, true).format("HH:mm");
    } else {
        return null;
    }
}

function toDate(dateString: moment.MomentInput): Date {
    return moment(dateString).toDate();
}

// Reason for formatting the todays date and provided date string
// We have to ensure the format of both dates are the same
// Otherwise isSameOrBefore doesnt parse the dates properly
//
// Returns true if the date is in the future
// Returns false if the date is in the past
function isInFuture(dateString: moment.MomentInput): boolean {
    const format = getDateFormatForMoment();

    const today = moment(new Date(), format);
    const date = moment(dateString, format);

    return today.isBefore(date);
}

function isInPast(date: moment.MomentInput): boolean {
    return moment().startOf("day").isAfter(date);
}

function isValidInterval(startDateString: string, endDateString: string): boolean {
    if (!isValidDateFormat(startDateString) || !isValidDateFormat(endDateString)) {
        return false;
    }

    const startDate = moment(startDateString, getDateFormatForMoment());
    const endDate = moment(endDateString, getDateFormatForMoment());

    return endDate.isBefore(startDate) === false;
}

function isValidIntervalV2(startDateString: string, endDateString: string): boolean {
    if (!isValidDateFormatV2(startDateString) || !isValidDateFormatV2(endDateString)) {
        return false;
    }

    const startDate = moment(startDateString, getDateFormatForMoment());
    const endDate = moment(endDateString, getDateFormatForMoment());

    return endDate.isBefore(startDate) === false;
}

function toShortDate(date: moment.MomentInput): string {
    return moment(date).format("YYMMDD");
}

function addDays(dateString: string, numberOfDays: number): string {
    const newDate = moment(dateString);
    newDate.add(numberOfDays, "days");
    return getFormattedDateString(newDate.toDate());
}

function getDifference(startDateString: string, endDateString: string, unit: moment.unitOfTime.Diff): number {
    const startDate = moment(startDateString);
    const endDate = moment(endDateString);
    return endDate.diff(startDate, unit);
}

function getMonths(format: string): string[] {
    if (!format) {
        format = "MMMM";
    }

    const m = moment();
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push(m.month(i).format(format));
    }
    return months;
}

function getPreviusAndCurrentMonthsAndYear(format: string, numberOfMonths: number) {
    if (!format) {
        format = "MMMM YYYY";
    }

    if (!numberOfMonths) {
        numberOfMonths = 12;
    }

    const months = [];
    for (let i = 0; i < numberOfMonths; i++) {
        const t = moment().subtract(i, "month");
        const date = moment([t.year(), t.month()]);
        months.push({
            monthValue: date.format("YYYY-MM-DD"),
            monthText: moment(date).format(format)[0].toUpperCase() + moment(date).format(format).substr(1)
        });
    }
    return months;
}

function getDateFormatForMoment(): string {
    const culture = environment.getCulture();

    switch (culture) {
        case "sv-FI":
        case "fi-FI":
            return "D.M.YYYY";
        case "en-GB":
            return "DD/MM/YYYY";
        case "nb-NO":
            return "DD.MM.YYYY";
        default:
            return "YYYY-MM-DD";
    }
}

//Returns the time in format hh:mm
function getTimeAsStringFromDate(date: Date): string {
    if (!date) return "";

    let hours = date.getHours().toString();
    let minutes = date.getMinutes().toString();
    if (date.getHours() === 0) hours = "00";
    else if (date.getHours() < 10) hours = "0" + date.getHours();

    if (date.getMinutes() === 0) minutes = "00";
    else if (date.getMinutes() < 10) minutes = "0" + date.getMinutes();

    return hours + ":" + minutes;
}

function isDateStringInRange(start: string, toBeCompared: string, end: string): boolean {
    if (!toBeCompared) return false;

    const dateFormat = getDateFormatForMoment();
    const startDate = start ? moment(start, dateFormat, true) : null;
    const toBeComparedDate = moment(toBeCompared, dateFormat, true);
    const endDate = end ? moment(end, dateFormat, true) : null;

    if (startDate?.isValid() === false || toBeComparedDate?.isValid() === false || endDate?.isValid() === false) {
        return false;
    }

    return (
        (!startDate || startDate.isSameOrBefore(toBeComparedDate)) &&
        (!endDate || toBeComparedDate.isSameOrBefore(endDate))
    );
}

function toLocalTimeFormat(timeString: string, strict: boolean, includeSeconds: boolean = false): string {
    const myMoment = moment.utc(timeString, serverSideTimeFormats, strict);
    const culture = environment.getCulture();
    myMoment.locale(culture);
    if (myMoment.isValid()) {
        return myMoment.format(includeSeconds ? "LTS" : "LT");
    }
    return timeString;
}

function serverDateTimeStringToMoment(timeString: string, strict: boolean): moment.Moment {
    let myMoment = moment.utc(timeString, serverSideDateTimeFormats, strict);
    if (!myMoment.isValid()) {
        myMoment = moment.utc(timeString, serverSideDateFormat, strict);
    }
    return myMoment;
}

function toLocalDateTimeFormat(dateTimeString: string, strict: boolean, includeSeconds: boolean = false): string {
    const myMoment = serverDateTimeStringToMoment(dateTimeString, strict);
    const culture = environment.getCulture();
    myMoment.locale(culture);
    if (myMoment.isValid()) {
        return myMoment.format(getDateFormatForMoment() + " " + (includeSeconds ? "LTS" : "LT"));
    }
    return dateTimeString;
}

function toLocalDateFormat(dateTimeString: string, strict: boolean): string {
    const myMoment = serverDateTimeStringToMoment(dateTimeString, strict);
    const culture = environment.getCulture();
    myMoment.locale(culture);
    if (myMoment.isValid()) {
        return myMoment.format(getDateFormatForMoment());
    }
    return dateTimeString;
}

function fromLocalTimeFormat(timeString: string, strict: boolean): string {
    const culture = environment.getCulture();
    const myMoment = moment.utc(timeString, "LT", culture, strict);
    if (myMoment.isValid()) {
        return myMoment.format(timeOutputFormat);
    }
    return timeString;
}

function fromLocalDateFormat(localDateString: string, strict: boolean): string {
    const culture = environment.getCulture();
    const myMoment = moment.utc(localDateString, getDateFormatForMoment(), culture, strict);
    if (myMoment.isValid()) {
        return myMoment.format(serverSideDateFormat);
    }
    return localDateString;
}

function getTodaysDate(): string {
    return moment().format(getDateFormatForMoment());
}

export default {
    completeDate: completeDate,
    formatIfComplete: formatIfComplete,
    calculateEndDate: calculateEndDate,
    getFormattedDateString: getFormattedDateString,
    getFormattedDateStringEmptyIfNonValidDate: getFormattedDateStringEmptyIfNonValidDate,
    isValidDateFormat: isValidDateFormat,
    isValidDateFormatV2: isValidDateFormatV2,
    isValidShortDateFormat: isValidShortDateFormat,
    isValidYearFormat: isValidYearFormat,
    isValidDateAndTimeFormat: isValidDateAndTimeFormat,
    isValidDateAndTimeFormatV2: isValidDateAndTimeFormatV2,
    isValidInterval: isValidInterval,
    isValidIntervalV2: isValidIntervalV2,
    toDate: toDate,
    toTime: toTime,
    getFormattedDateAndTimeString: getFormattedDateAndTimeString,
    isInFuture: isInFuture,
    isInPast: isInPast,
    toShortDate: toShortDate,
    addDays: addDays,
    getDifference: getDifference,
    getMonths: getMonths,
    getPreviusAndCurrentMonthsAndYear: getPreviusAndCurrentMonthsAndYear,
    getDateFormatForMoment: getDateFormatForMoment,
    isValidTimeFormat: isValidTimeFormat,
    getTimeAsStringFromDate: getTimeAsStringFromDate,
    isDateStringInRange: isDateStringInRange,
    toLocalTimeFormat: toLocalTimeFormat,
    toLocalDateFormat: toLocalDateFormat,
    toLocalDateTimeFormat: toLocalDateTimeFormat,
    fromLocalTimeFormat: fromLocalTimeFormat,
    fromLocalDateFormat: fromLocalDateFormat,
    getTodaysDate: getTodaysDate
};
